import { useState } from 'react';

export default function Services() {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: 'ri-road-map-line',
      title: 'Driveway Sealcoating',
      subtitle: 'Premium Protection',
      description: 'Professional asphalt emulsion sealcoating that creates a protective barrier against water, UV rays, and chemicals. Extends driveway life by 3–5 years.',
      features: [
        'Premium asphalt emulsion sealers',
        'UV protection coating',
        'Water damage prevention',
        '2-coat application system',
        'Professional equipment',
        '24-hour curing process'
      ],
      price: 'Starting at $0.20/sq ft'
    },
    {
      icon: 'ri-hammer-line',
      title: 'Crack Filling',
      subtitle: 'Structural Repair',
      description: 'Professional crack filling prevents water infiltration and freeze-thaw damage, extending the life of your asphalt and creating a smooth surface ready for sealing.',
      features: [
        'Hot rubberized crack filler',
        'Prevents water infiltration',
        'Stops freeze-thaw damage',
        'Smooth surface finish',
        'Works with any crack size',
        'Long-lasting results'
      ],
      price: 'Included in packages'
    },
    {
      icon: 'ri-brush-line',
      title: 'Full Maintenance Package',
      subtitle: 'Complete Care',
      description: 'Our all-in-one package combines crack filling and sealcoating for maximum protection. The best value for driveways that need comprehensive care.',
      features: [
        'Full driveway inspection',
        'Complete crack filling',
        'Surface preparation',
        'Premium sealer application',
        'Quality assurance check',
        'Complete cleanup'
      ],
      price: 'Best value — ask for quote'
    }
  ];

  const processSteps = [
    { step: '01', title: 'Inspection', description: 'Free detailed assessment of your driveway condition', icon: 'ri-search-line' },
    { step: '02', title: 'Preparation', description: 'Thorough cleaning and surface prep', icon: 'ri-tools-line' },
    { step: '03', title: 'Crack Repair', description: 'Fill and seal any existing cracks', icon: 'ri-hammer-line' },
    { step: '04', title: 'Sealcoating', description: 'Professional application of premium sealer', icon: 'ri-brush-line' },
    { step: '05', title: 'Quality Check', description: 'Final inspection and complete cleanup', icon: 'ri-checkbox-circle-line' }
  ];

  return (
    <section id="services" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <div className="text-orange-500 font-display font-bold text-lg uppercase tracking-widest mb-3">What We Do</div>
          <h2 className="font-display text-6xl md:text-7xl font-bold text-white uppercase mb-6">Our Services</h2>
          <p className="text-xl text-gray-400 max-w-2xl">
            Professional driveway sealcoating and crack repair that protects your investment across all of Walworth County.
          </p>
        </div>

        {/* Service Tabs */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-20">
          <div className="lg:col-span-2 flex flex-col gap-3">
            {services.map((service, index) => (
              <button
                key={index}
                onClick={() => setActiveService(index)}
                className={`flex items-center gap-4 p-5 rounded-xl text-left transition-all duration-300 border ${
                  activeService === index
                    ? 'bg-orange-500 border-orange-500 text-white shadow-lg shadow-orange-500/20'
                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${activeService === index ? 'bg-white/20' : 'bg-white/10'}`}>
                  <i className={`${service.icon} text-xl`}></i>
                </div>
                <div>
                  <div className="font-display font-bold text-lg uppercase">{service.title}</div>
                  <div className={`text-sm ${activeService === index ? 'text-orange-200' : 'text-gray-500'}`}>{service.subtitle}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-3 bg-white/5 border border-white/10 rounded-2xl p-8">
            <h3 className="font-display text-4xl font-bold text-white uppercase mb-2">{services[activeService].title}</h3>
            <p className="text-orange-500 font-semibold mb-6">{services[activeService].subtitle}</p>
            <p className="text-gray-300 mb-8 leading-relaxed text-lg">{services[activeService].description}</p>

            <div className="grid grid-cols-2 gap-3 mb-8">
              {services[activeService].features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-300">
                  <i className="ri-check-line text-orange-500 flex-shrink-0"></i>
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-white/10">
              <div className="text-orange-400 font-bold text-lg">{services[activeService].price}</div>
              <a
                href="#contact"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-bold transition-all duration-200 inline-flex items-center gap-2"
              >
                Get Quote <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Process Steps */}
        <div className="mb-20">
          <h3 className="font-display text-4xl font-bold text-white uppercase text-center mb-12">Our Professional Process</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative text-center group">
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-px bg-white/10" />
                )}
                <div className="relative mb-4">
                  <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto group-hover:border-orange-500 group-hover:bg-orange-500/10 transition-all duration-300">
                    <i className={`${step.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{step.step}</span>
                  </div>
                </div>
                <h4 className="font-display font-bold text-white text-lg uppercase mb-1">{step.title}</h4>
                <p className="text-gray-400 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-500 rounded-2xl p-12 text-center">
          <h3 className="font-display text-5xl font-bold text-white uppercase mb-4">Ready to Protect Your Driveway?</h3>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Get a free on-site estimate. We serve all of Walworth County and respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:262-215-0688" className="bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all duration-200 inline-flex items-center justify-center gap-2">
              <i className="ri-phone-fill"></i> Call (262) 215-0688
            </a>
            <a href="#contact" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-white hover:text-orange-600 transition-all duration-200 inline-flex items-center justify-center gap-2">
              <i className="ri-calculator-line"></i> Free Estimate Form
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
